import { Button, ButtonProps } from "@/components/ui/button";
import { toNumber } from "@/utils/number";
import { useField, useFormikContext } from "formik";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  memo
} from "react";
import { createPortal } from "react-dom";
// eslint-disable-next-line
// @ts-ignore
import get from "lodash/get";
import { cn } from "@/lib/utils";

const CLAMP_TOOLTIP_MS = 3000;
const CLAMP_TOOLTIP_GAP = 8;
const CLAMP_TOOLTIP_VIEWPORT_PAD = 8;

/** Only one QuantityField clamp tooltip should be visible at a time. */
const clampTooltipListeners = new Set<(sourceId: symbol) => void>();

const broadcastClampTooltipShow = (sourceId: symbol) => {
  clampTooltipListeners.forEach((listener) => listener(sourceId));
};

type QuantityFieldProps = {
  name: string;
  min?: number;
  max?: number;
  disabled?: boolean;
  withoutUndefined?: boolean;
  placeholder?: string;
  className?: string;
  size?: ButtonProps["size"];
  onChange?: (value: number | undefined) => void;
  fieldWidth?: number;
  inputClassName?: string;
  buttonClassName?: string;
  iconClassName?: string;
  /** When true (default), show a floating tooltip if the value is auto-clamped to min/max. */
  clampTooltipEnabled?: boolean;
};

const getClampMessage = (
  previous: number,
  limited: number,
  min: number | undefined,
  max: number | undefined
): string | null => {
  if (previous === limited) return null;
  if (typeof max === "number" && previous > max && limited === max) {
    return `Maximum available is ${max}`;
  }
  if (typeof min === "number" && previous < min && limited === min) {
    return `Minimum is ${min}`;
  }
  // Fallback when max/min math and hard floor of 1 interact
  if (typeof max === "number" && limited === max && previous > limited) {
    return `Maximum available is ${max}`;
  }
  if (typeof min === "number" && limited === min && previous < limited) {
    return `Minimum is ${min}`;
  }
  return null;
};

type TooltipCoords = {
  top: number;
  left: number;
  /** Distance from tooltip left edge to the field center (for the arrow). */
  arrowOffset: number;
  /** Show below the control when there isn't room above. */
  placeBelow: boolean;
};

export const QuantityField: React.FC<QuantityFieldProps> = memo(
  ({
    name,
    min = 0,
    max,
    disabled = false,
    placeholder,
    className = "",
    size: _size,
    onChange,
    withoutUndefined,
    inputClassName = "",
    buttonClassName = "",
    iconClassName = "",
    clampTooltipEnabled = true
  }) => {
    const [field] = useField(name);
    const { values, setFieldValue } = useFormikContext();
    const isFocusedRef = useRef(false);
    const instanceIdRef = useRef(Symbol(name));
    const rootRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [clampMessage, setClampMessage] = useState<string | null>(null);
    const [tooltipCoords, setTooltipCoords] = useState<TooltipCoords | null>(null);

    // Callers (and API fields like num_cards) sometimes pass string bounds.
    const minBound = typeof min === "number" && !isNaN(min) ? min : Number(min);
    const maxBound =
      max === undefined || max === null || max === ""
        ? undefined
        : typeof max === "number" && !isNaN(max)
          ? max
          : Number(max);
    const resolvedMin = !isNaN(minBound) ? minBound : 0;
    const resolvedMax =
      typeof maxBound === "number" && !isNaN(maxBound) ? maxBound : undefined;

    const rawValue = (values as { [k: string]: unknown })[name] ?? get(values, name);
    const parsedValue = parseInt(String(rawValue ?? ""), 10);
    const hasNumericValue = !isNaN(parsedValue);
    const value = hasNumericValue ? parsedValue : resolvedMin;

    const hideClampTooltip = useCallback(() => {
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = null;
      }
      setClampMessage(null);
      setTooltipCoords(null);
    }, []);

    const showClampTooltip = useCallback(
      (message: string) => {
        if (!clampTooltipEnabled) return;
        // Dismiss any other QuantityField clamp tooltips first.
        broadcastClampTooltipShow(instanceIdRef.current);
        if (dismissTimerRef.current) {
          clearTimeout(dismissTimerRef.current);
        }
        setClampMessage(message);
        dismissTimerRef.current = setTimeout(() => {
          setClampMessage(null);
          setTooltipCoords(null);
          dismissTimerRef.current = null;
        }, CLAMP_TOOLTIP_MS);
      },
      [clampTooltipEnabled]
    );

    // Hide when another QuantityField shows its clamp tooltip.
    useEffect(() => {
      const listener = (sourceId: symbol) => {
        if (sourceId !== instanceIdRef.current) {
          hideClampTooltip();
        }
      };
      clampTooltipListeners.add(listener);
      return () => {
        clampTooltipListeners.delete(listener);
      };
    }, [hideClampTooltip]);

    // Hide on any click/tap outside this field (covers switching between steppers).
    useEffect(() => {
      if (!clampMessage) return;

      const onPointerDown = (event: PointerEvent) => {
        const target = event.target as Node | null;
        if (target && rootRef.current?.contains(target)) return;
        hideClampTooltip();
      };

      document.addEventListener("pointerdown", onPointerDown, true);
      return () => {
        document.removeEventListener("pointerdown", onPointerDown, true);
      };
    }, [clampMessage, hideClampTooltip]);

    // Portal + fixed positioning escapes parent overflow. Align to the *right* of the
    // stepper so the bubble grows left over form content (steppers sit on the row's
    // right edge next to summary panels that would cover a centered tooltip).
    useLayoutEffect(() => {
      if (!clampMessage) {
        setTooltipCoords(null);
        return;
      }

      const updatePosition = () => {
        const root = rootRef.current;
        const tip = tooltipRef.current;
        if (!root || !tip) return;

        const rect = root.getBoundingClientRect();
        const tipWidth = tip.offsetWidth;
        const tipHeight = tip.offsetHeight;
        const pad = CLAMP_TOOLTIP_VIEWPORT_PAD;

        // Right-align to the control, then keep fully inside the viewport.
        let left = rect.right - tipWidth;
        left = Math.max(pad, Math.min(left, window.innerWidth - tipWidth - pad));

        let placeBelow = false;
        let top = rect.top - CLAMP_TOOLTIP_GAP - tipHeight;
        if (top < pad) {
          placeBelow = true;
          top = rect.bottom + CLAMP_TOOLTIP_GAP;
        }

        const fieldCenterX = rect.left + rect.width / 2;
        const arrowOffset = Math.min(
          Math.max(fieldCenterX - left, 12),
          tipWidth - 12
        );

        setTooltipCoords({ top, left, arrowOffset, placeBelow });
      };

      updatePosition();
      // Second pass after paint in case fonts/layout settle width.
      const rafId = requestAnimationFrame(updatePosition);
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);
      return () => {
        cancelAnimationFrame(rafId);
        window.removeEventListener("resize", updatePosition);
        window.removeEventListener("scroll", updatePosition, true);
      };
    }, [clampMessage]);

    useEffect(() => {
      return () => {
        if (dismissTimerRef.current) {
          clearTimeout(dismissTimerRef.current);
        }
      };
    }, []);

    const getValueWithLimits = useCallback(
      (next: number, includeMin?: boolean) => {
        if (next === undefined || isNaN(next)) {
          return withoutUndefined ? 0 : undefined;
        }

        let result = next;
        result = Math.max(1, result);
        if (includeMin && typeof resolvedMin === "number") {
          result = Math.max(resolvedMin, result);
        }
        if (typeof resolvedMax === "number") {
          result = Math.min(resolvedMax, result);
        }

        return result;
      },
      [resolvedMin, resolvedMax, withoutUndefined]
    );

    const applyLimitedValue = useCallback(
      (
        previous: number | undefined,
        limited: number | undefined,
        options?: { notifyOnChange?: boolean; showTooltip?: boolean }
      ) => {
        const { notifyOnChange = true, showTooltip = true } = options ?? {};
        setFieldValue(name, limited);
        if (notifyOnChange) {
          onChange?.(limited);
        }

        if (
          showTooltip &&
          typeof previous === "number" &&
          typeof limited === "number" &&
          !isNaN(previous) &&
          !isNaN(limited)
        ) {
          const message = getClampMessage(previous, limited, resolvedMin, resolvedMax);
          if (message) {
            showClampTooltip(message);
          }
        }
      },
      [name, setFieldValue, onChange, resolvedMin, resolvedMax, showClampTooltip]
    );

    // Clamp out-of-range numeric values when min/max change, but never while the user is
    // mid-edit with an empty/partial field — that caused jumps (e.g. clear → snap to max/min).
    useEffect(() => {
      if (isFocusedRef.current) return;
      if (rawValue === "" || rawValue === undefined || rawValue === null) return;
      if (!hasNumericValue) return;

      const limitedValue = getValueWithLimits(parsedValue, true);
      if (typeof limitedValue === "number" && limitedValue !== parsedValue) {
        applyLimitedValue(parsedValue, limitedValue, { notifyOnChange: false });
      }
    }, [
      name,
      resolvedMin,
      resolvedMax,
      rawValue,
      parsedValue,
      hasNumericValue,
      getValueWithLimits,
      applyLimitedValue
    ]);

    const inputDisplayValue =
      rawValue === "" || rawValue === undefined || rawValue === null
        ? ""
        : String(rawValue);

    return (
      <div
        ref={rootRef}
        className={cn("relative inline-flex", className)}
        data-testid="QuantityField"
      >
        {clampMessage &&
          typeof document !== "undefined" &&
          createPortal(
            <div
              ref={tooltipRef}
              role="status"
              data-testid="QuantityField-clamp-tooltip"
              className="pointer-events-none fixed z-[999999] whitespace-nowrap rounded-[8px] bg-[#0a0a0a] px-2.5 py-1.5 text-[12px] leading-[16px] text-white shadow-[0px_2px_8px_0px_rgba(0,0,0,0.16)] dark:bg-white dark:text-[#0a0a0a]"
              style={{
                top: tooltipCoords?.top ?? 0,
                left: tooltipCoords?.left ?? 0,
                opacity: tooltipCoords ? 1 : 0
              }}
            >
              {clampMessage}
              {tooltipCoords && (
                <span
                  aria-hidden
                  className={cn(
                    "absolute h-0 w-0 border-[5px] border-transparent",
                    tooltipCoords.placeBelow
                      ? "bottom-full border-b-[#0a0a0a] dark:border-b-white"
                      : "top-full border-t-[#0a0a0a] dark:border-t-white"
                  )}
                  style={{ left: tooltipCoords.arrowOffset, transform: "translateX(-50%)" }}
                />
              )}
            </div>,
            document.body
          )}
        <div className="flex items-center overflow-hidden rounded-[8px] border border-border bg-background">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn(
              "h-[36px] w-[36px] shrink-0 rounded-none border-r border-border px-[8px]",
              buttonClassName
            )}
            onClick={(e) => {
              e?.stopPropagation();
              const previous = toNumber(value);
              const attempted = previous - 1;
              const newValue = getValueWithLimits(attempted, true);
              if (
                typeof newValue === "number" &&
                newValue === previous &&
                attempted < previous
              ) {
                showClampTooltip(`Minimum is ${resolvedMin}`);
                return;
              }
              hideClampTooltip();
              applyLimitedValue(previous, newValue, { showTooltip: false });
            }}
            disabled={disabled}
          >
            <svg
              viewBox="0 0 10.875 1.875"
              className={cn("w-[11px]", iconClassName)}
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.9375 0.9375H0.9375"
                stroke="currentColor"
                strokeWidth="1.875"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Button>

          <input
            type="text"
            inputMode="numeric"
            className={cn(
              "border-none w-[40px] min-w-0 flex-1 bg-transparent px-4 py-0 text-center text-[14px] font-medium leading-[20px] text-foreground outline-none focus:outline-none disabled:text-muted-foreground",
              inputClassName
            )}
            style={{ boxShadow: "none" }}
            name={field.name}
            value={inputDisplayValue}
            placeholder={placeholder}
            onFocus={() => {
              isFocusedRef.current = true;
              hideClampTooltip();
            }}
            onBlur={(e) => {
              isFocusedRef.current = false;
              const previous = parseInt(e.target.value, 10);
              const newValue = getValueWithLimits(previous, true);
              const next = newValue ?? resolvedMin;
              applyLimitedValue(isNaN(previous) ? undefined : previous, next);
              field.onBlur(e);
            }}
            onKeyDown={(e) => {
              e?.stopPropagation();
              if (!e.code || e.key.length !== 1) return;
              if (!/^\d$/.test(e.key)) {
                e.preventDefault();
                return;
              }
            }}
            onChange={(e) => {
              e?.stopPropagation();
              const raw = e.target.value;
              if (raw === "") {
                setFieldValue(name, "");
                onChange?.(undefined);
                return;
              }
              if (!/^\d+$/.test(raw)) return;
              const parsed = parseInt(raw, 10);
              // Enforce max while typing so values can't exceed bounds, but defer min
              // until blur so clearing / multi-digit entry (e.g. 1 → 11) works.
              const next =
                typeof resolvedMax === "number" && !isNaN(parsed)
                  ? Math.min(resolvedMax, parsed)
                  : parsed;
              applyLimitedValue(parsed, next);
            }}
            disabled={disabled}
          />

          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn(
              "h-[36px] w-[36px] shrink-0 rounded-none border-l border-border px-[8px]",
              buttonClassName
            )}
            onClick={(e) => {
              e?.stopPropagation();
              const previous = toNumber(value);
              const attempted = previous + 1;
              const newValue = getValueWithLimits(attempted, true);
              if (
                typeof newValue === "number" &&
                newValue === previous &&
                attempted > previous
              ) {
                if (typeof resolvedMax === "number") {
                  showClampTooltip(`Maximum available is ${resolvedMax}`);
                }
                return;
              }
              hideClampTooltip();
              applyLimitedValue(previous, newValue, { showTooltip: false });
            }}
            disabled={disabled}
          >
            <svg
              viewBox="0 0 10.875 10.875"
              className={cn("w-[11px]", iconClassName)}
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.9375 5.43919H0.9375"
                stroke="currentColor"
                strokeWidth="1.875"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M5.42969 0.9375V9.9375"
                stroke="currentColor"
                strokeWidth="1.875"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Button>
        </div>
      </div>
    );
  }
);
